import { useEffect, useRef, useState } from 'react';

import { expandCoinoneTickerFrame, normalizeCoinoneWsTicker } from '../lib/normalize';
import { coinoneSocketManager } from '../lib/coinoneSocket';
import { useRealtimeGaveUpToast } from './useRealtimeGaveUpToast';

import type { NormalizedTicker } from '../types/exchange';

interface UseCoinoneWebSocketProps {
  markets: string[];
  onTickerUpdate: (ticker: NormalizedTicker) => void;
  enabled?: boolean;
}

/**
 * TRAD-3635 — 코인원 WS 메시지 → NormalizedTicker. DATA·TICKER 가 아닌 프레임(CONNECTED·SUBSCRIBED·PONG·ERROR)은 null.
 * 워커는 Blob 으로, 폴백(SocketManager)은 문자열로 줄 수 있어 둘 다 받는다.
 */
export const parseCoinoneMessage = async (data: string | ArrayBuffer | Blob): Promise<NormalizedTicker | null> => {
  try {
    // 파서가 Blob 에 요구하는 계약은 `text()` 하나다(useBithumbWebSocket 테스트와 같은 계약 — jsdom Blob 에는 text() 가 없다).
    const text =
      typeof data === 'string'
        ? data
        : typeof (data as Blob).text === 'function'
          ? await (data as Blob).text()
          : new TextDecoder().decode(data as ArrayBuffer);
    const frame: unknown = JSON.parse(text);
    if ((frame as { response_type?: string })?.response_type === 'ERROR') {
      // 🔴 거래소가 요청을 거부한 것 — 시세가 멈춘 원인이 여기 있으므로 삼키지 않는다.
      console.error('[useCoinoneWebSocket] coinone ERROR frame', frame);
      return null;
    }
    const d = expandCoinoneTickerFrame(frame);
    return d ? normalizeCoinoneWsTicker(d) : null;
  } catch (error) {
    // 🔴 raw error를 삼키지 않는다(useBithumbWebSocket 과 같은 규칙).
    console.error('[useCoinoneWebSocket] ticker parse failed', error);
    return null;
  }
};

/** 빗썸 훅(useBithumbWebSocket)과 같은 모양 — 구독 형식 차이는 워커/폴백이 흡수한다. */
export const useCoinoneWebSocket = ({ markets, onTickerUpdate, enabled = true }: UseCoinoneWebSocketProps) => {
  const onTickerUpdateRef = useRef(onTickerUpdate);
  const [isConnected, setIsConnected] = useState(false);

  useRealtimeGaveUpToast(coinoneSocketManager, enabled && markets.length > 0);

  useEffect(() => {
    onTickerUpdateRef.current = onTickerUpdate;
  }, [onTickerUpdate]);

  // 의존성은 «종목 목록의 값»이어야 한다(배열 참조가 매 렌더 바뀌어도 재구독하지 않게). 목록은 키에서 다시 만든다 —
  // 빗썸 훅의 `markets.join(',')` 를 의존성 배열에 직접 쓰는 형태는 exhaustive-deps 경고 2개를 낸다.
  const marketsKey = markets.join(',');

  useEffect(() => {
    const list = marketsKey ? marketsKey.split(',') : [];
    if (!enabled || list.length === 0) return;

    const handleMessage = async (data: string | ArrayBuffer | Blob) => {
      const ticker = await parseCoinoneMessage(data);
      if (ticker) {
        onTickerUpdateRef.current(ticker);
      }
    };

    const unsubscribe = coinoneSocketManager.subscribe('ticker', list, handleMessage);
    const unlistenConnection = coinoneSocketManager.onConnectionChange(setIsConnected);
    setIsConnected(coinoneSocketManager.connected);

    return () => {
      unsubscribe();
      unlistenConnection();
    };
  }, [enabled, marketsKey]);

  return { isConnected };
};
